// Third-party imports
import { useRef } from 'react';
import {
  TextInput,
  SelectInput,
  AutocompleteArrayInput,
  NumberInput,
  useEditContext,
  useGetOne,
  required,
  TabbedForm,
  FormTab,
  ListButton,
  CanAccess,
} from 'react-admin';
import { Typography, Divider, Box, Chip, Button } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';

// Local imports - types
import type { ValidCodeNameType } from '@src/types';
import type { PartnerSettings } from '@src/settings';
import { DashboardResources } from '@src/types';

// Local imports - components
import VariantsSection from './VariantsSection';
import MediaTab from './MediaTab';
import AvailabilityInput from './AvailabilityInput';
import type { VariantSaveRef } from './VariantDetailPanel';

// Custom header that appears at the top with Parent SKU and buttons
const CatalogFormHeader = ({
  parentSku,
  formRef,
  variantSaveRef
}: {
  parentSku?: string;
  formRef: React.RefObject<HTMLFormElement | null>;
  variantSaveRef: VariantSaveRef;
}) => {
  const handleSave = () => {
    if (variantSaveRef?.current) {
      void variantSaveRef.current();
    } else if (formRef.current) {
      formRef.current.requestSubmit();
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        px: 2,
        py: 2,
        mb: 2,
        borderBottom: '1px solid',
        borderColor: 'divider',
        backgroundColor: 'background.paper',
        position: 'sticky',
        top: 0,
        zIndex: 10,
      }}
    >
      {parentSku && (
        <Chip
          label={`Parent SKU: ${parentSku}`}
          variant="outlined"
          size="medium"
        />
      )}
      <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
        <ListButton label="Back" />
        <CanAccess resource={DashboardResources.CatalogItems} action="edit">
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
          >
            Save
          </Button>
        </CanAccess>
      </Box>
    </Box>
  );
};

// Form component that can access the record through Edit context
const CatalogForm = () => {
  const { record } = useEditContext();
  const attributes = record?.attributeData;
  // Ref to the TabbedForm's <form> element so the header save button can
  // trigger submission without fragile DOM queries. We use a callback ref
  // on a wrapper div and look up the form within it, scoped to this component.
  const formRef = useRef<HTMLFormElement | null>(null);

  // Shared ref that lets the Variants tab register its save handler so the
  // toolbar's SaveButton can trigger a variant save (with optional impact
  // confirmation) instead of the default parent-form save. The ref is null
  // when no variant is selected or when a non-Variants tab is active.
  const variantSaveRef: VariantSaveRef = useRef(null);

  const { data: settings } = useGetOne<PartnerSettings & { id: number }>('settings', { id: 0 });
  const currencyCode = settings?.primaryCurrencyCode || 'USD';

  // Create choices for select inputs
  const departmentChoices = attributes?.validDepartments?.map((dept: string) => ({
    id: dept,
    name: dept,
  })) || [];

  const categoryChoices = attributes?.validCategories?.map((cat: string) => ({
    id: cat,
    name: cat,
  })) || [];

  const subcategoryChoices = attributes?.validSubcategories?.map((subcat: string) => ({
    id: subcat,
    name: subcat,
  })) || [];

  const genderChoices = attributes?.validGenders?.map((gender: ValidCodeNameType) => ({
    id: gender.code,
    name: gender.name,
  })) || [];

  const rubricCodeChoices = attributes?.validRubricCodes?.map((code: string) => ({
    id: code,
    name: code,
  })) || [];

  return (
    <>
      <CatalogFormHeader parentSku={record?.id} formRef={formRef} variantSaveRef={variantSaveRef} />
      <div ref={(el) => {
        formRef.current = el?.querySelector('form') ?? null;
      }}>
      <TabbedForm toolbar={false} formRootPathname="">
        <FormTab label="Parent Details">
        {/* Basic Information */}
        <Typography variant="h6" gutterBottom sx={{ width: '100%' }}>
          Basic Information
        </Typography>
        <TextInput
          source="productName"
          label="Product Name"
          fullWidth
          validate={required()}
        />
        <TextInput
          source="productDescription"
          label="Product Description"
          fullWidth
          multiline
          rows={4}
        />
        <TextInput
          source="brandDisplay"
          label="Brand Display"
          fullWidth
        />

        <Divider sx={{ my: 3, width: '100%' }} />

        {/* Categorization */}
        <Typography variant="h6" gutterBottom sx={{ width: '100%' }}>
          Categorization
        </Typography>
        <AutocompleteArrayInput
          source="departments"
          label="Departments"
          choices={departmentChoices}
          fullWidth
        />
        <AutocompleteArrayInput
          source="categories"
          label="Categories"
          choices={categoryChoices}
          fullWidth
        />
        <AutocompleteArrayInput
          source="subcategories"
          label="Subcategories"
          choices={subcategoryChoices}
          fullWidth
        />
        <SelectInput
          source="gender.code"
          label="Gender"
          choices={genderChoices}
          optionText="name"
          optionValue="id"
          fullWidth
        />

        <Divider sx={{ my: 3, width: '100%' }} />

        {/* Pricing & Eligibility */}
        <Typography variant="h6" gutterBottom sx={{ width: '100%' }}>
          Pricing & Eligibility
        </Typography>
        <NumberInput
          source="msrpPrice"
          label={`MSRP/RRP (${currencyCode})`}
          step={0.01}
          min={0}
          fullWidth
        />
        <NumberInput
          source="lastKnownRetailPrice"
          label={`Last Known Price (${currencyCode})`}
          step={0.01}
          min={0}
          fullWidth
        />
        <TextInput
          source="taxCode"
          label="Tax Code"
          fullWidth
        />
        <AvailabilityInput
          source="availability"
          label="Eligibility"
        />
        <SelectInput
          source="rubricCode"
          label="Inspection Category (Rubric Code)"
          choices={rubricCodeChoices}
          optionText="name"
          optionValue="id"
          fullWidth
        />
      </FormTab>

      <FormTab label="Media">
        <MediaTab />
      </FormTab>

      <FormTab label="Variants">
        <VariantsSection variantSaveRef={variantSaveRef} />
      </FormTab>
      </TabbedForm>
      </div>
    </>
  );
};

export default CatalogForm;
